import { FC, lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { SpinnerOverlay } from './components/OverlaySpinner'

// import { MainScreen } from './sections/MainScreen'
import { Menu } from './sections/Menu' // Menu is the most used section. Delaying it affects ux
// import { Order } from './sections/Order'
import { Wishlist } from './featureSliced/padges/Wishlist'
import { NetworkError } from 'featureSliced/padges/NetworkError' // NetworkError must be loaded in case there is not connection
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { handleLocalStorage } from './featureSliced/shared/utils/handleLocalStorage'

import { Bucket } from 'featureSliced/padges/Bucket'
import { MainScreen } from 'featureSliced/padges/MainScreen'
import { Payment } from 'featureSliced/padges/Payment'
import { CheckoutQRM } from 'featureSliced/padges/CheckoutQRM'

const Wallet = lazy((): any => import('./featureSliced/padges/Wallet'))
const Welcome = lazy((): any => import('./featureSliced/padges/Welcome'))

type AppRouterProps = {
  initialLoading?: () => void
  initLoadMenuData?: () => Promise<void>
}

export const AppRouter: FC<AppRouterProps> = ({
  initialLoading,
  initLoadMenuData,
}) => {
  const paymentVariant = useMySelector((state) => state.app.menu.paymentVariant)
  const { bookingVariant } = useMySelector((state) => state.app.menu)
  const isAppInit = useMySelector((state) => state.app.isAppInit)
  const { registerId, dataEnc } = handleLocalStorage(['registerId', 'dataEnc'])

  const isQrMenu = process.env.REACT_APP_IS_CLIC === '0'
  // ordering exists in one mode only; view-only, pay-only, view-and-pay
  // and the legacy empty values all mean no basket
  const isOrderingAvailable = isQrMenu && bookingVariant === 2
  const isConfigPending = isQrMenu && !isAppInit

  const handleInitialLoading = async () => {
    initialLoading && (await initialLoading())
  }

  const handleInitLoadMenuData = async () => {
    initLoadMenuData && (await initLoadMenuData())
  }

  return (
    <Suspense fallback={<SpinnerOverlay isLoading />}>
      <Routes>
        <Route path="/" element={<MainScreen />} />
        <Route path="/network-error" element={<NetworkError />} />

        <Route
          path="/wishlist"
          element={
            <PrivateRoute isAllowed={registerId || dataEnc}>
              <Wishlist />
            </PrivateRoute>
          }
        />
        <Route
          path="/welcome"
          element={<Welcome loadApp={handleInitialLoading} />}
        />
        {/* Online table reservation is discontinued (2026-10): a booking made
            here reached no one — the restaurant e-mail was switched off in
            2025-04, the staff app in 2025-02 — while the guest still got an SMS.
            Old links land on the menu, where the sidebar keeps the phone number.
            Reservations move to runmyhub. */}
        <Route path="/reservation" element={<Navigate to="/" replace />} />
        <Route path="/booking" element={<Navigate to="/" replace />} />
        <Route
          path="/order"
          // create QR-Menu order. no payment
          element={
            <PrivateRoute
              isPending={isConfigPending}
              isAllowed={isOrderingAvailable}
            >
              <Bucket />
            </PrivateRoute>
          }
        />

        <Route
          path="/checkout"
          // pay for QR-Menu order if it is not yet created
          // or regular CC checkout
          element={
            <PrivateRoute
              isPending={isConfigPending}
              isAllowed={
                // unavailable for pay AFTER meal
                paymentVariant !== 2 && isOrderingAvailable
              }
            >
              <CheckoutQRM />
            </PrivateRoute>
          }
        />

        <Route
          path="/payment"
          // pay for QR-Menu order if it was already createed
          element={
            <PrivateRoute
              isAllowed={
                // unavailable for pay BEFORE meal
                paymentVariant !== 1 || process.env.REACT_APP_IS_CLIC === '1'
              }
            >
              <Payment />
            </PrivateRoute>
          }
        />

        <Route
          path="/menu"
          element={
            <PrivateRoute
              isAllowed={
                bookingVariant !== 3 || process.env.REACT_APP_IS_CLIC === '1'
              }
            >
              <Menu loadMenuData={handleInitLoadMenuData} />
            </PrivateRoute>
          }
        />

        <Route
          path="/wallet"
          element={
            <PrivateRoute isAllowed={process.env.REACT_APP_IS_CLIC === '1'}>
              <Wallet />
            </PrivateRoute>
          }
        >
          <Route path="" />
          <Route path="card" />
          <Route path="orders" />
          {/* <Route path="refill" /> */}
          <Route path="scanner" />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

const PrivateRoute: FC<any> = ({
  isAllowed = true,
  isPending = false,
  children,
}) => {
  // an empty route while the config loads: rendering the page lets its own
  // effects redirect, blocking it throws the guest off a legal url
  if (isPending) {
    return null
  }

  if (isAllowed) {
    return children
  }

  return <Navigate to="/" />
}
